import type { GameType } from '@big2/shared';
import type { InfoPageType } from './components/InfoPages.js';

export type AppRoute =
  | { type: 'home'; gameType?: GameType }
  | { type: 'room'; roomId: string }
  | { type: 'profile'; tab?: 'profile' | 'friends' | 'leaderboard' | 'settings' }
  | { type: 'cardBacks' }
  | { type: 'history' }
  | { type: 'rules'; gameType: GameType }
  | { type: 'info'; page: InfoPageType }
  | { type: 'payments' }
  | { type: 'admin' }
  | { type: 'notFound'; path: string };

export function parseRoute(pathname: string, search = ''): AppRoute {
  if (search) {
    try {
      const qRoom = new URLSearchParams(search).get('room');
      if (qRoom && qRoom.trim()) {
        return { type: 'room', roomId: qRoom.trim().toUpperCase() };
      }
    } catch {}
  }
  if (!pathname || pathname === '/') return { type: 'home' };
  const p = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  if (p === '/') return { type: 'home' };

  // Room / Game deep links: /room/:roomId or /join/:roomId
  const decodedPath = (() => {
    try {
      return decodeURIComponent(pathname);
    } catch {
      return pathname;
    }
  })();
  const roomMatch = decodedPath.match(/^\/(?:room|join)\/([\p{L}\p{N} _-]{2,20})/iu);
  if (roomMatch && roomMatch[1]) {
    return { type: 'room', roomId: roomMatch[1].trim().toUpperCase() };
  }

  // Dedicated game landing routes (SEO & deep links)
  if (p === '/big2' || p === '/arvan-gurav' || p === '/arvangurav') {
    return { type: 'home', gameType: 'big2' };
  }
  if (p === '/muushig') {
    return { type: 'home', gameType: 'muushig' };
  }
  if (p === '/twentyone' || p === '/21' || p === '/ochko') {
    return { type: 'home', gameType: 'twentyone' };
  }
  if (p === '/daaluu') {
    return { type: 'home', gameType: 'daaluu' };
  }

  if (p === '/settings' || p === '/account') {
    return { type: 'profile', tab: 'settings' };
  }
  if (p === '/profile') {
    return { type: 'profile', tab: 'profile' };
  }
  if (p === '/friends' || p === '/friend') {
    return { type: 'profile', tab: 'friends' };
  }
  if (p === '/leaderboard' || p === '/ranks') {
    return { type: 'profile', tab: 'leaderboard' };
  }
  if (p === '/history' || p === '/room-history') {
    return { type: 'history' };
  }
  if (p === '/card-backs' || p === '/cardbacks' || p === '/cards') {
    return { type: 'cardBacks' };
  }
  if (p === '/payments') {
    return { type: 'payments' };
  }
  if (p === '/admin') {
    return { type: 'admin' };
  }
  if (p === '/rules/daaluu' || p === '/daaluu/rules') {
    return { type: 'rules', gameType: 'daaluu' };
  }
  if (p === '/rules/muushig' || p === '/muushig/rules') {
    return { type: 'rules', gameType: 'muushig' };
  }
  if (
    p === '/rules/twentyone' ||
    p === '/twentyone/rules' ||
    p === '/rules/21' ||
    p === '/21/rules' ||
    p === '/rules/ochko'
  ) {
    return { type: 'rules', gameType: 'twentyone' };
  }
  if (p === '/rules' || p === '/how-to-play' || p === '/rules/big2' || p === '/big2/rules') {
    return { type: 'rules', gameType: 'big2' };
  }
  if (p === '/privacy' || p === '/privacy-policy') {
    return { type: 'info', page: 'privacy' };
  }
  if (p === '/terms' || p === '/terms-of-service') {
    return { type: 'info', page: 'terms' };
  }
  if (p === '/subscription' || p === '/vip' || p === '/benefits') {
    return { type: 'info', page: 'subscription' };
  }

  return { type: 'notFound', path: pathname };
}

export function routeToPath(route: AppRoute): string {
  switch (route.type) {
    case 'home':
      return route.gameType && route.gameType !== 'big2' ? `/${route.gameType}` : '/';
    case 'room':
      return `/room/${route.roomId}`;
    case 'profile':
      return route.tab === 'leaderboard' ? '/leaderboard' : route.tab === 'friends' ? '/friends' : route.tab === 'settings' ? '/settings' : '/profile';
    case 'cardBacks':
      return '/card-backs';
    case 'history':
      return '/history';
    case 'rules':
      return `/rules/${route.gameType}`;
    case 'info':
      return `/${route.page}`;
    case 'payments':
      return '/payments';
    case 'admin':
      return '/admin';
    case 'notFound':
      return route.path || '/404';
  }
}

export function isModalRoute(route: AppRoute): boolean {
  return (
    route.type === 'cardBacks' ||
    route.type === 'history' ||
    route.type === 'rules'
  );
}
